import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import './WorkTypeToggle.css';

// V71.3.2 — üst çubukta çalışma şekli: yer değişince (ofisten eve, evden ofise) yeniden giriş gerekmeden
// güncellenir; İK › Evden Çalışma saatleri bu seçimden itibaren hesaplar. 5 dk'da bir yenilenir.
const AD = { office: 'Ofis', remote: 'Remote' };
const IKON = { office: '🏢', remote: '🏠' };
const OFIS_AG_NOT = 'Ofis ağına bağlısınız; Remote seçilemez.';

export default function WorkTypeSwitch() {
  const [tur, setTur] = useState(null);
  const [yuklendi, setYuklendi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sor, setSor] = useState(false); // V71.6.2 — açık kalan oturumla yeni günün ilk girişi: seçim modalı
  const [ofisAg, setOfisAg] = useState(false); // V71.26.3 — ofis ağındayken Remote kapalı (backend de reddeder)

  const yukle = useCallback(() => authAPI.getWorkType()
    .then(r => { const t = r.data?.work_type || null; setTur(t); setSor(!t); setOfisAg(!!r.data?.ofis_ag); })
    .catch(() => {})
    .finally(() => setYuklendi(true)), []);
  useEffect(() => {
    yukle();
    const t = setInterval(yukle, 5 * 60 * 1000);
    window.addEventListener('focus', yukle); // V71.31.7 — ağ değişince (ofis → ev) kilit bayat kalmasın
    return () => { clearInterval(t); window.removeEventListener('focus', yukle); };
  }, [yukle]);

  const sec = async (yeni) => {
    if (busy || yeni === tur) return;
    if (yeni === 'remote' && ofisAg) { // V71.31.7 — kilit yerine anlık yeniden kontrol; hâlâ ofis ağıysa uyarı
      const r = await authAPI.getWorkType().catch(() => null);
      const hala = r ? !!r.data?.ofis_ag : true;
      setOfisAg(hala);
      if (hala) { toast.warning(OFIS_AG_NOT); return; }
    }
    if (tur) {
      const soru = `Çalışma şekli ${AD[yeni]} olarak değiştirilsin mi? Saat hesabı şu andan itibaren ${AD[yeni]} sayılır.`;
      const ok = typeof window.showConfirm === 'function' ? await window.showConfirm(soru) : window.confirm(soru);
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await authAPI.setWorkType(yeni);
      setTur(r.data?.work_type || yeni);
      setSor(false);
      toast.success(`Çalışma şekli: ${AD[yeni]}${r.data?.saat ? ` (${r.data.saat})` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Çalışma şekli değiştirilemedi.');
    } finally { setBusy(false); }
  };

  if (!yuklendi) return null;
  if (!tur) {
    return (
      <>
      {sor && createPortal(
        <div style={S.overlay}>
          <div style={S.modal} role="dialog" aria-modal="true" aria-label="Bugünkü çalışma şekli">
            <div style={S.header}>
              <h2 style={S.title}>Bugün nereden çalışıyorsunuz?</h2>
              <p style={S.subtitle}>Yeni mesai gününün ilk girişi. Çalışma şeklinizi seçin; gün içinde üst çubuktan değiştirebilirsiniz.</p>
            </div>
            <div style={S.body} className="wt-toggle">
              {['office', 'remote'].map(v => (
                <button key={v} type="button" className="wt-opt" disabled={busy} onClick={() => sec(v)}
                  title={v === 'remote' && ofisAg ? OFIS_AG_NOT : undefined}>
                  <span className="wt-ic" aria-hidden="true">{IKON[v]}</span>{AD[v]}
                </button>
              ))}
            </div>
          </div>
        </div>, document.body)}
      <span className="wt-switch-pair" role="group" aria-label="Bugünkü çalışma şekli seçilmedi">
        {['office', 'remote'].map(v => (
          <button key={v} type="button" className="wt-switch" disabled={busy} onClick={() => sec(v)}
            title={v === 'remote' && ofisAg ? OFIS_AG_NOT : `Bugün ${AD[v]} çalışıyorum`}>
            {IKON[v]} {AD[v]}
          </button>
        ))}
      </span>
      </>
    );
  }
  const diger = tur === 'remote' ? 'office' : 'remote';
  return (
    <button type="button" className={`wt-switch ${tur}`} disabled={busy} onClick={() => sec(diger)}
      title={diger === 'remote' && ofisAg ? `Bugün: ${AD[tur]} — ${OFIS_AG_NOT}` : `Bugün: ${AD[tur]} — ${AD[diger]} olarak değiştirmek için tıklayın`}>
      {IKON[tur]} {AD[tur]}
    </button>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'var(--bg-overlay, rgba(15,23,42,0.55))', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99990,
  },
  modal: { background: 'var(--bg-elevated, #fff)', color: 'var(--fg-primary)', borderRadius: 16, width: 400, maxWidth: '92vw', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' },
  header: { background: 'var(--grad-brand)', color: '#fff', padding: '20px 24px 16px' },
  title: { margin: 0, fontSize: 19, fontWeight: 700 },
  subtitle: { margin: '6px 0 0', fontSize: 13, opacity: 0.85, lineHeight: '19px' },
  body: { padding: '20px 24px 24px' },
};
